import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { WORLD_LOCATIONS, WORLD_EVENTS, LOOT_TABLE, type WorldEvent } from '@/data/worldSystem';
import { MapPin, Compass, Backpack, AlertCircle, Sparkles, Zap } from 'lucide-react';

interface OpenWorldExplorerProps {
  currentLocation: string;
  onEncounter: (enemyId: string) => void;
  onNavigate: (locationId: string) => void;
}

export const OpenWorldExplorer: React.FC<OpenWorldExplorerProps> = ({
  currentLocation,
  onEncounter,
  onNavigate,
}) => {
  const [exploring, setExploring] = useState(false);
  const [currentEvent, setCurrentEvent] = useState<WorldEvent | null>(null);
  const [collectedItems, setCollectedItems] = useState<string[]>([]);
  const [eventLog, setEventLog] = useState<string[]>(['Ready to explore...']);

  const location = WORLD_LOCATIONS.find(
    (loc) => loc.id === currentLocation.toLowerCase().replace(/\s+/g, '-')
  );

  const addLog = (message: string) => {
    setEventLog((prev) => [...prev.slice(-5), message]);
  };

  const triggerRandomEvent = () => {
    const randomEvent = WORLD_EVENTS[Math.floor(Math.random() * WORLD_EVENTS.length)];
    if (Math.random() < randomEvent.probability) {
      setCurrentEvent(randomEvent);
      addLog(`Event: ${randomEvent.name}`);
      return randomEvent;
    }
    return null;
  };

  const handleExplore = () => {
    setExploring(true);
    addLog('You begin exploring the area...');

    setTimeout(() => {
      const event = triggerRandomEvent();

      if (!event) {
        // Find random loot
        if (location) {
          const randomLoot = location.loot[Math.floor(Math.random() * location.loot.length)];
          setCollectedItems((prev) => [...prev, randomLoot]);
          addLog(`Found: ${randomLoot}!`);
        }
      } else if (event.type === 'combat') {
        addLog('Enemies appear! Prepare for battle!');
        if (location) {
          const enemy = location.enemies[Math.floor(Math.random() * location.enemies.length)];
          setTimeout(() => {
            onEncounter(enemy);
            setExploring(false);
          }, 500);
        }
      } else if (event.type === 'treasure') {
        addLog('Gold and treasure discovered!');
        if (event.reward) {
          setCollectedItems((prev) => [...prev, `Gold x${event.reward.gold}`]);
        }
      } else if (event.type === 'npc') {
        addLog('A mysterious figure appears...');
      } else {
        addLog('You discover something interesting...');
      }

      setExploring(false);
    }, 1500);
  };

  const handleMove = (targetLocationId: string) => {
    addLog(`Traveling to ${WORLD_LOCATIONS.find((l) => l.id === targetLocationId)?.name}...`);
    setTimeout(() => {
      onNavigate(targetLocationId);
      setCollectedItems([]);
      setCurrentEvent(null);
      addLog('Arrived at new location.');
    }, 1000);
  };

  const nearbyLocations = WORLD_LOCATIONS.filter((loc) => loc.id !== location?.id).slice(0, 3);

  return (
    <div className="space-y-4">
      {/* Location Header */}
      <Card className="p-6 bg-gradient-to-r from-orange-900/20 to-slate-800 border-orange-500 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MapPin className="text-orange-500 w-6 h-6" />
            <div>
              <h2 className="text-2xl font-bold text-orange-500">{currentLocation}</h2>
              {location && (
                <p className="text-gray-400 text-sm">
                  {location.type.charAt(0).toUpperCase() + location.type.slice(1)} • Levels {location.levelRange[0]}-
                  {location.levelRange[1]}
                </p>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className="text-gray-400 text-sm">Weather</p>
            <p className="text-white font-semibold capitalize">{location?.weather}</p>
          </div>
        </div>

        {/* Weather Effect */}
        {location?.weather === 'stormy' && (
          <div className="bg-red-900/30 border border-red-600 rounded p-3 flex items-start gap-2">
            <AlertCircle className="text-red-400 w-5 h-5 flex-shrink-0 mt-0.5" />
            <p className="text-red-300 text-sm">
              Stormy weather! Combat difficulty increased. Visibility low.
            </p>
          </div>
        )}
        {location?.weather === 'dark' && (
          <div className="bg-purple-900/30 border border-purple-600 rounded p-3 flex items-start gap-2">
            <Zap className="text-purple-400 w-5 h-5 flex-shrink-0 mt-0.5" />
            <p className="text-purple-300 text-sm">Darkness shrouds the area. Strange energies abound.</p>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Exploration Panel */}
        <div className="lg:col-span-2 space-y-4">
          {/* Main Exploration */}
          <Card className="p-6 bg-slate-800 border-orange-500 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Compass className="text-orange-500 w-5 h-5" />
              <h3 className="text-xl font-bold text-orange-500">Exploration</h3>
            </div>

            {currentEvent && (
              <div className="bg-slate-700 border-l-4 border-yellow-500 p-4 rounded">
                <h4 className="text-yellow-400 font-bold mb-2">{currentEvent.name}</h4>
                <p className="text-gray-300 text-sm mb-4">{currentEvent.description}</p>
              </div>
            )}

            <div className="space-y-2">
              <Button
                onClick={handleExplore}
                disabled={exploring}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold h-12"
              >
                {exploring ? 'Exploring...' : 'Explore Area'}
              </Button>
              <p className="text-gray-400 text-xs text-center">Discover items, encounter enemies, find treasure</p>
            </div>
          </Card>

          {/* Event Log */}
          <Card className="p-4 bg-slate-800 border-gray-700 space-y-3">
            <h3 className="text-orange-400 font-bold text-sm">Event Log</h3>
            <div className="bg-slate-900 rounded p-3 h-32 overflow-y-auto space-y-1">
              {eventLog.map((log, idx) => (
                <p key={idx} className="text-gray-400 text-xs font-mono">
                  &gt; {log}
                </p>
              ))}
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Collected Items */}
          <Card className="p-4 bg-slate-800 border-green-600 space-y-3">
            <div className="flex items-center gap-2">
              <Backpack className="text-green-500 w-5 h-5" />
              <h3 className="text-green-400 font-bold text-sm">Collected Items ({collectedItems.length})</h3>
            </div>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {collectedItems.length > 0 ? (
                collectedItems.map((item, idx) => (
                  <div key={idx} className="bg-slate-700 p-2 rounded text-xs text-gray-300 flex justify-between">
                    <span>{item}</span>
                    <span className="text-green-400">
                      +{LOOT_TABLE[item as keyof typeof LOOT_TABLE]?.value || 0}g
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-xs italic">No items collected yet</p>
              )}
            </div>
            {collectedItems.length > 0 && (
              <Button
                size="sm"
                className="w-full bg-green-600 hover:bg-green-700 text-white text-xs h-8"
              >
                Claim Rewards
              </Button>
            )}
          </Card>

          {/* Travel */}
          <Card className="p-4 bg-slate-800 border-blue-600 space-y-3">
            <h3 className="text-blue-400 font-bold text-sm">Nearby Locations</h3>
            <div className="space-y-2">
              {nearbyLocations.map((loc) => (
                <Button
                  key={loc.id}
                  onClick={() => handleMove(loc.id)}
                  variant="outline"
                  className="w-full justify-start text-xs border-gray-600 text-gray-300 hover:text-white hover:border-blue-500"
                >
                  <MapPin className="w-3 h-3 mr-2" />
                  {loc.name}
                </Button>
              ))}
            </div>
          </Card>

          {/* Location Info */}
          <Card className="p-4 bg-slate-800 border-yellow-600 space-y-3">
            <div className="flex items-center gap-2">
              <Sparkles className="text-yellow-500 w-4 h-4" />
              <h3 className="text-yellow-400 font-bold text-sm">Location Info</h3>
            </div>
            {location && (
              <div className="space-y-2 text-xs text-gray-300">
                <div>
                  <p className="text-gray-400">Type</p>
                  <p className="capitalize font-semibold">{location.type}</p>
                </div>
                <div>
                  <p className="text-gray-400">Enemies</p>
                  <p className="text-red-400 font-semibold">{location.enemies.join(', ')}</p>
                </div>
                <div>
                  <p className="text-gray-400">Common Loot</p>
                  <div className="text-green-400 space-y-0.5">
                    {location.loot.map((l) => (
                      <p key={l} className="text-xs">
                        • {l}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};
